import React from "react";
import Button from "@mui/material/Button";
import MenuItem from "@mui/material/MenuItem";
import CheckBox from "@mui/icons-material/CheckBox";
import CheckBoxOutlineBlank from "@mui/icons-material/CheckBoxOutlineBlank";
import ExpandMore from "@mui/icons-material/ExpandMore";
import MenuSurface from "../ui/MenuSurface";
import ListSubheader from "@mui/material/ListSubheader";
import TextField from "@mui/material/TextField";

export default function MultiSelectDropdown({
  label,
  options,
  selected,
  onChange,
  className = "",
  allLabel = "All",
  showLabel = true,
  creatable = false,
  createLabel = "Create",
  onCreate,
  showSelected = false,
  buttonId,
  disabled = false,
  searchable = false,
}) {
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [query, setQuery] = React.useState("");
  const listRef = React.useRef(null);
  const optionsId = React.useId();
  const close = () => {
    setAnchorEl(null);
    setQuery("");
  };
  const visibleOptions = searchable
    ? options.filter((option) =>
        (typeof option === "string" ? option : option.label)
          .toLocaleLowerCase()
          .includes(query.trim().toLocaleLowerCase()),
      )
    : options;
  const match = options.find(
    (option) =>
      (typeof option === "string" ? option : option.value) === selected[0],
  );
  const summary = !selected.length
    ? allLabel
    : showSelected && selected.length === 1
      ? typeof match === "string"
        ? match
        : match?.label || selected[0]
      : `${selected.length} Selected`;
  const display = [
    showLabel && !(showSelected && selected.length === 1) && label,
    summary,
  ]
    .filter(Boolean)
    .join(": ");
  const toggle = (value) =>
    onChange(
      selected.includes(value)
        ? selected.filter((item) => item !== value)
        : [...selected, value],
    );
  return (
    <div className={`dropdown ${className}`}>
      <Button
        id={buttonId}
        className="dropdown-toggle"
        disabled={disabled}
        endIcon={<ExpandMore />}
        aria-label={
          !showLabel ? `${label}${summary ? `: ${summary}` : ""}` : undefined
        }
        aria-haspopup="menu"
        aria-expanded={!!anchorEl}
        aria-controls={anchorEl ? optionsId : undefined}
        onClick={(event) => setAnchorEl(event.currentTarget)}
      >
        <span className="dropdown-summary">{display}</span>
      </Button>
      {anchorEl && (
        <MenuSurface
          id={optionsId}
          anchorEl={anchorEl}
          onClose={close}
          listRef={listRef}
          autoFocus={!searchable}
          fitBelow
          paperSx={
            className.split(/\s+/).includes("filter-dropdown")
              ? { borderRadius: "4px" }
              : undefined
          }
        >
          {searchable && (
            <ListSubheader sx={{ py: 1, lineHeight: "normal" }}>
              <TextField
                autoFocus
                fullWidth
                type="search"
                placeholder={`Search ${label.toLowerCase()}…`}
                value={query}
                disabled={disabled}
                onChange={(event) => setQuery(event.target.value)}
                onKeyDown={(event) => {
                  if (["Escape", "Tab"].includes(event.key)) return;
                  // Editing keys belong to the search field, not MenuList type-ahead.
                  event.stopPropagation();
                  if (event.key === "ArrowDown") {
                    event.preventDefault();
                    listRef.current
                      ?.querySelector('[role^="menuitem"]:not(:disabled)')
                      ?.focus();
                  }
                }}
                slotProps={{
                  htmlInput: {
                    "aria-label": `Search ${label}`,
                    "aria-controls": optionsId,
                  },
                }}
              />
            </ListSubheader>
          )}
          {searchable && visibleOptions.length === 0 && (
            <ListSubheader disableSticky sx={{ lineHeight: "normal", py: 2 }}>
              <span role="status">No matching options</span>
            </ListSubheader>
          )}
          {visibleOptions.map((option) => {
            const value = typeof option === "string" ? option : option.value;
            const text = typeof option === "string" ? option : option.label;
            const checked = selected.includes(value);
            return (
              <MenuItem
                key={value}
                component="button"
                type="button"
                role="menuitemcheckbox"
                aria-checked={checked}
                selected={checked}
                disabled={disabled}
                onClick={() => toggle(value)}
              >
                {checked ? (
                  <CheckBox sx={{ mr: 1, flexShrink: 0 }} />
                ) : (
                  <CheckBoxOutlineBlank sx={{ mr: 1, flexShrink: 0 }} />
                )}
                {text}
              </MenuItem>
            );
          })}
          {creatable && (
            <MenuItem
              component="button"
              type="button"
              disabled={disabled}
              onClick={() => {
                close();
                onCreate();
              }}
            >
              {createLabel}
            </MenuItem>
          )}
        </MenuSurface>
      )}
    </div>
  );
}
